import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MotionConfig } from 'motion/react';
import { Toaster } from 'sonner';

import { AuthProvider } from '@/features/auth/auth-context';
import { routeTree } from './routeTree.gen';
import '@/styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Produce data isn't collaborative — refetching every time someone
      // alt-tabs back is a request for nothing.
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry a 4xx: a 401 or a 403 will still be a 401 or a 403 on
        // the third attempt, and retrying just delays the error the user
        // needs to see.
        const status = (error as { status?: number })?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // The loader already resolved during hover-preload; re-running it on click
  // makes the navigation feel slower than not preloading at all.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root not found in index.html');

createRoot(rootElement).render(
  <StrictMode>
    {/* reducedMotion="user" makes Motion honour prefers-reduced-motion
        globally — transform and layout animations are skipped, opacity still
        fades. Set once here so no component has to remember. */}
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterProvider router={router} />
          <Toaster
            position="bottom-center"
            toastOptions={{
              className: 'font-sans',
              style: { borderRadius: '12px', border: '1px solid oklch(0.92 0.008 86)' },
            }}
          />
        </AuthProvider>
      </QueryClientProvider>
    </MotionConfig>
  </StrictMode>,
);
